"use client"

import { VideoRelationType } from "@/app/types/video"
import { alpha, Box, colors, Grid2 as Grid, Typography } from "@mui/material"
import CenterBox from "@v2/components/styled/CenterBox"
import VideoStream from "@v2/components/VideoStream"
import { uiColors } from "@v2/lib/color"
import { Play, Video } from "lucide-react"
import { useState } from "react"

export default function PdpVideos({ videos }: { videos: VideoRelationType[] }) {
    const sorted = [...videos].sort((a, b) => a.priority - b.priority)
    const [activeIndex, setActiveIndex] = useState(0)
    const activeVideo = sorted[activeIndex]

    if (!activeVideo) return null

    const isSingle = sorted.length === 1

    return (
        <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 2 }}>
                <Box sx={{ flexShrink: 0, display: 'flex' }}>
                    <Video size={20} color={colors.grey[700]} />
                </Box>
                <Typography sx={{ fontSize: 18, fontWeight: 500, color: colors.grey[800] }}>
                    ویدیوها
                </Typography>
            </Box>

            <Grid container spacing={2}>
                {!isSingle && (
                    <Grid size={{ xs: 12, md: 4 }} sx={{ order: { xs: 2, md: 1 } }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            overflowY: 'auto',
                            maxHeight: 420,
                        }}>
                            {sorted.map((video, index) => {
                                const isActive = index === activeIndex
                                return (
                                    <Box
                                        key={video.id}
                                        onClick={() => setActiveIndex(index)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            py: 1.25,
                                            px: 1.5,
                                            borderRadius: 1.5,
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                            bgcolor: isActive ? alpha(uiColors.primary, 0.08) : 'transparent',
                                            position: 'relative',
                                            transition: '250ms',
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: 8,
                                                borderRadius: 1.5,
                                                zIndex: 0,
                                                transition: '150ms',
                                            },
                                            '&:hover': {
                                                '&::after': !isActive ? {
                                                    inset: 4,
                                                    bgcolor: alpha(uiColors.primary, 0.06),
                                                } : {},
                                            },
                                        }}
                                    >
                                        <CenterBox sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                            bgcolor: isActive ? uiColors.primary : colors.grey[200],
                                            transition: '150ms',
                                        }}>
                                            <Play
                                                size={14}
                                                color={isActive ? '#fff' : colors.grey[600]}
                                            />
                                        </CenterBox>
                                        <Typography sx={{
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: isActive ? uiColors.primary : 'inherit',
                                            lineHeight: 1.6,
                                            transition: '150ms',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {video.title}
                                        </Typography>
                                    </Box>
                                )
                            })}
                        </Box>
                    </Grid>
                )}

                <Grid size={{ xs: 12, md: isSingle ? 12 : 8 }} sx={{ order: { xs: 1, md: 2 } }}>
                    <VideoStream slug={activeVideo.slug} title={activeVideo.title} poster={activeVideo.poster} />
                    {isSingle && (
                        <Typography sx={{ fontSize: 14, color: colors.grey[600], mt: 1 }}>
                            {activeVideo.title}
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </Box>
    )
}
